import React, { useState, useRef, useEffect } from 'react'
import bootstrapScopedStyles from '../../styles/bootstrapScoped.min.module.css'
import formStyles from './../../branding/formStyles'
import { BiInfoCircle } from 'react-icons/bi'
import { AsYouType } from 'libphonenumber-js'
import intlTelInput from 'intl-tel-input'
import colors from '../../branding/colors'
import customFormStyles from '../../styles/formStyles.module.css'

function OnfsInput({
  name,
  label,
  placeholder = `Enter ${label}`,
  error,
  type,
  required = false,
  handleChange,
  style,
  wasSubmitted,
  defaultValue = '',
  info,
  parentRef = useRef(),
  ...rest
}) {
  const isPhoneNumber = type === 'phone'
  const isCheckbox = type === 'checkbox'
  const isRadio = type === 'radio'
  const isTextArea = type === 'textarea'
  const isRequired = required
  const [isChecked, setIsChecked] = useState(false)
  const [inputValue, setInputValue] = useState(defaultValue)
  const [inputWasTouched, setInputWasTouched] = useState(false)
  const [isInfoHintVisible, setIsInfoHintVisible] = useState(false)
  const [lastKeyPressed, setLastKeyPressed] = useState(null)
  const [phoneInfo, setPhoneInfo] = useState(null)
  const [internationalPhoneInput, setInternationalPhoneInput] = useState(null)
  const [triggerRefreshPhoneInfo, setTriggerRefreshPhoneInfo] = useState(false)
  const ref = parentRef
  const wrapperRef = useRef()

  useEffect(() => {
    if (
      (isCheckbox || isRadio) &&
      (defaultValue === true || defaultValue?.toLowerCase() === 'true')
    ) {
      ref.current.click()
    } else if (isPhoneNumber) {
      // Init and store intl-tel-input
      setInternationalPhoneInput(
        intlTelInput(ref.current, {
          customContainer: `${bootstrapScopedStyles['form-control']} ${bootstrapScopedStyles['p-0']}`
        })
      )
    }
  }, [])

  // Save off phone info
  useEffect(() => {
    if (internationalPhoneInput) {
      setPhoneInfo(internationalPhoneInput.getSelectedCountryData())
    }
  }, [internationalPhoneInput, triggerRefreshPhoneInfo])

  return (
    <div
      ref={wrapperRef}
      style={style}
      className={
        isCheckbox || isRadio
          ? bootstrapScopedStyles['form-check']
          : bootstrapScopedStyles['form-group']
      }
    >
      {!isCheckbox && !isRadio && (
        <label
          style={formStyles.label}
          htmlFor={name}
          className={`${isRequired ? customFormStyles.required : ''}`}
        >
          {label}
        </label>
      )}

      {!isTextArea && !isPhoneNumber && (
        <div>
          <span style={{ display: 'flex', flexDirection: 'row' }}>
            <input
              {...rest}
              type={type}
              ref={ref}
              name={name}
              id={name}
              className={
                isCheckbox || isRadio
                  ? bootstrapScopedStyles['form-check-input']
                  : bootstrapScopedStyles['form-control']
              }
              placeholder={placeholder}
              value={isCheckbox ? isChecked : inputValue}
              onBlur={() => setInputWasTouched(true)}
              aria-describedby={
                (error && (wasSubmitted || inputWasTouched)
                  ? name + '-error'
                  : null,
                isInfoHintVisible ? name + '-info' : null)
              }
              aria-invalid={
                error && (wasSubmitted || inputWasTouched) ? 'true' : 'false'
              }
              aria-required={isRequired}
              onChange={(e) => {
                e.persist()
                // Set input value
                if (isCheckbox || isRadio) {
                  setIsChecked(!isChecked)
                } else {
                  setInputValue(ref.current.value)
                }

                // Run through default onChange Handler (errors)
                // If it is a radio element, let the parent group wrapper handle errors
                if (!isRadio) {
                  setTimeout(function () {
                    handleChange(e.target)
                  }, 0)
                }
              }}
            />
            {info !== undefined && (
              <button
                onClick={() => setIsInfoHintVisible(!isInfoHintVisible)}
                type='button'
                aria-label={`Provide a hint on ${label}`}
                aria-controls={name + '-info'}
                className={bootstrapScopedStyles['form-control']}
                style={{ maxWidth: '50px' }}
                title={`Provide a hint on ${label}`}
              >
                <BiInfoCircle
                  style={{
                    height: '100%',
                    width: '100%',
                    color: colors.primary
                  }}
                />
              </button>
            )}
          </span>
          {isInfoHintVisible && (
            <div
              id={name + '-info'}
              className={`${bootstrapScopedStyles.alert} ${bootstrapScopedStyles['alert-info']}`}
              style={{ marginBottom: '0px' }}
            >
              {info}
            </div>
          )}
        </div>
      )}

      {isPhoneNumber && (
        <div>
          <span style={{ display: 'flex', flexDirection: 'row' }}>
            <input
              autoComplete='phone'
              {...rest}
              type={type}
              className={bootstrapScopedStyles['form-control']}
              ref={ref}
              name={name}
              id={name}
              placeholder={placeholder}
              value={inputValue}
              onBlur={() => setInputWasTouched(true)}
              onFocus={() => {
                setTriggerRefreshPhoneInfo(!triggerRefreshPhoneInfo) // Trigger useEffect to get country data, since iti uses jquery :(
              }}
              aria-describedby={
                (error && (wasSubmitted || inputWasTouched)
                  ? name + '-error'
                  : null,
                isInfoHintVisible ? name + '-info' : null)
              }
              aria-invalid={
                error && (wasSubmitted || inputWasTouched) ? 'true' : 'false'
              }
              aria-required={isRequired}
              onKeyDown={(e) => setLastKeyPressed(e.key)}
              onChange={(e) => {
                // Persist needed for handleChange from within set timeout
                e.persist()

                let num = ref.current.value
                if (inputValue.length === 0) {
                  num = `+${phoneInfo.dialCode}${num}`
                }
                let newNum = new AsYouType(phoneInfo.iso2.toUpperCase()).input(
                  num
                )
                if (
                  (lastKeyPressed === 'Backspace' ||
                    lastKeyPressed === 'Delete') &&
                  inputValue === newNum
                ) {
                  // If the formatter added the parentheses back in, remove them (when deleting)
                  newNum = newNum.slice(1, -1)
                }

                setInputValue(newNum)

                // Run through default onChange Handler
                setTimeout(function () {
                  handleChange(e.target)
                }, 0)
              }}
              style={{
                paddingLeft: '50px',
                border: 'none',
                width: '100%',
                height: '100%'
              }}
            />
            {info !== undefined && (
              <button
                onClick={() => setIsInfoHintVisible(!isInfoHintVisible)}
                type='button'
                aria-label={`Provide a hint on ${label}`}
                aria-controls={name + '-info'}
                className={bootstrapScopedStyles['form-control']}
                style={{ maxWidth: '50px' }}
                title={`Provide a hint on ${label}`}
              >
                <BiInfoCircle
                  style={{
                    height: '100%',
                    width: '100%',
                    color: colors.primary
                  }}
                />
              </button>
            )}
          </span>
          {isInfoHintVisible && (
            <div
              id={name + '-info'}
              className={`${bootstrapScopedStyles.alert} ${bootstrapScopedStyles['alert-info']}`}
              style={{ marginBottom: '0px' }}
            >
              {info}
            </div>
          )}
        </div>
      )}

      {isTextArea && (
        <textarea
          {...rest}
          className={bootstrapScopedStyles['form-control']}
          rows='3'
          placeholder={placeholder}
          type={type}
          ref={ref}
          name={name}
          id={name}
          value={inputValue}
          onBlur={() => setInputWasTouched(true)}
          onChange={(e) => {
            e.persist()
            setInputValue(ref.current.value)
            // Run through default onChange Handler (errors)
            handleChange(e.target)
          }}
          aria-describedby={
            error && (wasSubmitted || inputWasTouched) ? name + '-error' : null
          }
          aria-invalid={
            error && (wasSubmitted || inputWasTouched) ? 'true' : 'false'
          }
          aria-required={isRequired}
        />
      )}

      {(isCheckbox || isRadio) && (
        <label
          className={`${bootstrapScopedStyles['form-check-label']} ${
            isRequired ? customFormStyles.required : ''
          }`}
          htmlFor={name}
        >
          {label}
        </label>
      )}

      {error && (wasSubmitted || inputWasTouched) && (
        <div
          role='alert'
          id={name + '-error'}
          className={`${bootstrapScopedStyles.alert} ${bootstrapScopedStyles['alert-danger']}`}
        >
          {error}
        </div>
      )}
    </div>
  )
}

export default OnfsInput
